import { useRef, useState } from 'react';
import { uploadPostCover } from '../../lib/queries';
import {
  requestStreamUploadUrl,
  uploadToStream,
  createVideoMedia,
  MAX_VIDEO_BYTES,
} from '../../lib/stream-queries';
import type { WizardMediaItem } from '../../lib/stream-queries';
import './MediaUploader.css';

/** A single tile in the uploader's ordered list. While uploading it has no
 *  resolved id/url yet; once done it carries everything needed to emit a
 *  WizardMediaItem. */
export interface MediaEntry {
  /** Stable client-side id (for keys + reorder), unrelated to the media row. */
  id: string;
  kind: 'image' | 'video';
  /** Local object URL for preview (image thumb, or video poster fallback). */
  previewUrl: string;
  filename: string;
  status: 'uploading' | 'ready' | 'error';
  /** 0..1 while uploading. */
  progress: number;
  error?: string;
  /** Resolved on success. images → url; videos → mediaId. */
  url?: string;
  mediaId?: string;
  aspectRatio?: number | null;
  /** Videos only: custom thumbnail shown in the player instead of the first
   *  frame. Written to that video's media.thumbnail_url on publish/save. */
  thumbUrl?: string | null;
  thumbUploading?: boolean;
  thumbError?: string;
}

interface Props {
  authorId: string;
  items: MediaEntry[];
  onChange: (next: MediaEntry[]) => void;
  /** Optional aspect-ratio hint stamped on new media rows. Omit to let media
   *  keep its natural ratio (the viewer fits any aspect). */
  aspectRatio?: number;
  disabled?: boolean;
  /** Bubbled up so the parent can show the Stream "not configured" notice. */
  onStreamNotConfigured?: () => void;
  /** Generous cap on total items. */
  max?: number;
}

let _seq = 0;
function nextId(): string {
  _seq += 1;
  return `m_${Date.now().toString(36)}_${_seq}`;
}

/** Human-readable GiB for size-limit messaging (e.g. 30 GB). */
function formatGiB(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024 * 1024))} GB`;
}

/**
 * Measure a video file's true aspect ratio (width / height) from its metadata,
 * client-side, before upload. We stamp this on the media row so a portrait clip
 * is stored as portrait (e.g. ~0.56) instead of defaulting to landscape — the
 * player then renders a correctly-shaped box with no black bars, full-height on
 * mobile. Resolves null if it can't be read (caller falls back to the hint).
 */
function measureVideoAspect(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    const done = (ar: number | null) => { URL.revokeObjectURL(url); resolve(ar); };
    v.onloadedmetadata = () => {
      done(v.videoWidth > 0 && v.videoHeight > 0 ? v.videoWidth / v.videoHeight : null);
    };
    v.onerror = () => done(null);
    v.src = url;
  });
}

/**
 * Multi-file, mixed image+video uploader producing an ORDERED list the user
 * can reorder and remove from. Images upload via uploadPostCover; videos via
 * the Cloudflare Stream direct-creator flow with per-item progress. The first
 * item is the cover. Emits MediaEntry[]; the parent maps ready entries to
 * WizardMediaItem[] for adminCreateWizardPost.
 */
export function MediaUploader({
  authorId,
  items,
  onChange,
  aspectRatio,
  disabled,
  onStreamNotConfigured,
  max = 20,
}: Props) {
  const input = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);

  // Always operate on the latest list via a ref so concurrent uploads don't
  // clobber each other's state updates.
  const itemsRef = useRef(items);
  itemsRef.current = items;

  function patch(id: string, fields: Partial<MediaEntry>) {
    const next = itemsRef.current.map((it) => (it.id === id ? { ...it, ...fields } : it));
    itemsRef.current = next;
    onChange(next);
  }

  function append(entry: MediaEntry) {
    const next = [...itemsRef.current, entry];
    itemsRef.current = next;
    onChange(next);
  }

  async function uploadOne(entry: MediaEntry, file: File) {
    try {
      if (entry.kind === 'image') {
        patch(entry.id, { progress: 0.4 });
        const url = await uploadPostCover({ authorId, file });
        patch(entry.id, { status: 'ready', progress: 1, url, aspectRatio });
      } else {
        // Fail fast on oversized files before touching the network.
        if (file.size > MAX_VIDEO_BYTES) {
          patch(entry.id, {
            status: 'error',
            error: `Video is too large (max ${formatGiB(MAX_VIDEO_BYTES)}).`,
          });
          return;
        }
        // Read the clip's real aspect up front so portrait videos are stored
        // portrait (no landscape default → no black bars). Falls back to the
        // caller's hint if the file can't be measured.
        const measured = await measureVideoAspect(file);
        const videoAspect = measured ?? aspectRatio ?? null;
        const { uploadURL, uid } = await requestStreamUploadUrl(file.size);
        await uploadToStream({
          uploadURL,
          file,
          onProgress: (f) => patch(entry.id, { progress: f }),
        });
        const { mediaId } = await createVideoMedia({
          uid,
          uploadedBy: authorId,
          aspectRatio: videoAspect,
        });
        patch(entry.id, { status: 'ready', progress: 1, mediaId, aspectRatio: videoAspect });
      }
    } catch (err: any) {
      console.error('[MediaUploader] upload failed', err);
      if (err?.code === 'stream_not_configured' || err?.message === 'stream_not_configured') {
        onStreamNotConfigured?.();
        patch(entry.id, {
          status: 'error',
          error: "Video uploads aren't available right now.",
        });
      } else if (err?.code === 'file_too_large' || err?.message === 'file_too_large') {
        patch(entry.id, {
          status: 'error',
          error: `Video is too large (max ${formatGiB(MAX_VIDEO_BYTES)}).`,
        });
      } else {
        patch(entry.id, { status: 'error', error: err?.message ?? 'Upload failed.' });
      }
    }
  }

  function addFiles(files: File[]) {
    if (disabled) return;
    const room = max - itemsRef.current.length;
    const picked = files.slice(0, Math.max(0, room));
    for (const file of picked) {
      const isVideo = file.type.startsWith('video/');
      const entry: MediaEntry = {
        id: nextId(),
        kind: isVideo ? 'video' : 'image',
        previewUrl: URL.createObjectURL(file),
        filename: file.name,
        status: 'uploading',
        progress: 0,
      };
      append(entry);
      void uploadOne(entry, file);
    }
  }

  /** Upload a custom thumbnail for one VIDEO entry (shown as its player
   *  poster instead of the first frame). */
  async function uploadThumb(entry: MediaEntry, file: File | null) {
    if (!file) return;
    patch(entry.id, { thumbUploading: true, thumbError: undefined });
    try {
      const url = await uploadPostCover({ authorId, file });
      patch(entry.id, { thumbUrl: url, thumbUploading: false });
    } catch (err: any) {
      console.error('[MediaUploader] thumbnail upload failed', err);
      patch(entry.id, {
        thumbUploading: false,
        thumbError: err?.message ?? 'Thumbnail upload failed.',
      });
    }
  }

  function remove(id: string) {
    const target = itemsRef.current.find((it) => it.id === id);
    if (target) URL.revokeObjectURL(target.previewUrl);
    const next = itemsRef.current.filter((it) => it.id !== id);
    itemsRef.current = next;
    onChange(next);
  }

  function move(index: number, dir: -1 | 1) {
    const next = [...itemsRef.current];
    const to = index + dir;
    if (to < 0 || to >= next.length) return;
    [next[index], next[to]] = [next[to], next[index]];
    itemsRef.current = next;
    onChange(next);
  }

  // ---------- drag-to-reorder ----------
  const dragIndex = useRef<number | null>(null);
  function onItemDragStart(i: number) {
    dragIndex.current = i;
  }
  function onItemDrop(i: number) {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === i) return;
    const next = [...itemsRef.current];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    itemsRef.current = next;
    onChange(next);
  }

  const atCap = items.length >= max;

  return (
    <div className="mediaup">
      {items.length > 0 && (
        <ol className="mediaup__list">
          {items.map((it, i) => (
            <li
              key={it.id}
              className={`mediaup__tile mediaup__tile--${it.status}`}
              draggable={!disabled && it.status === 'ready'}
              onDragStart={() => onItemDragStart(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onItemDrop(i)}
            >
              <div className="mediaup__thumb">
                {it.kind === 'image' ? (
                  <img src={it.previewUrl} alt="" />
                ) : it.thumbUrl ? (
                  // Custom video thumbnail stands in for the clip preview so
                  // the tile shows what the player will show.
                  <img src={it.thumbUrl} alt="" />
                ) : (
                  <video src={it.previewUrl} muted playsInline preload="metadata" />
                )}
                <span className="mediaup__kind">{it.kind === 'video' ? 'Video' : 'Image'}</span>
                {i === 0 && <span className="mediaup__cover">Cover</span>}
              </div>

              {it.status === 'uploading' && (
                <div className="mediaup__bar" aria-hidden="true">
                  <div
                    className="mediaup__bar-fill"
                    style={{ width: `${Math.round(it.progress * 100)}%` }}
                  />
                </div>
              )}
              {it.status === 'uploading' && (
                <p className="mediaup__status">Uploading… {Math.round(it.progress * 100)}%</p>
              )}
              {it.status === 'error' && (
                <p className="mediaup__status mediaup__status--error">{it.error}</p>
              )}
              {it.status === 'ready' && (
                <p className="mediaup__status mediaup__status--ok" title={it.filename}>
                  {it.filename}
                </p>
              )}

              {/* Per-video thumbnail: the image shown in the player instead of
                  the clip's first frame. */}
              {it.kind === 'video' && it.status === 'ready' && (
                <div className="mediaup__vthumb">
                  <label className={`mediaup__vthumb-btn ${disabled || it.thumbUploading ? 'mediaup__vthumb-btn--off' : ''}`}>
                    {it.thumbUploading ? 'Uploading…' : it.thumbUrl ? 'Replace thumbnail' : '+ Thumbnail'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      disabled={disabled || it.thumbUploading}
                      onChange={(e) => {
                        void uploadThumb(it, e.target.files?.[0] ?? null);
                        e.currentTarget.value = '';
                      }}
                    />
                  </label>
                  {it.thumbUrl && !it.thumbUploading && (
                    <button
                      type="button"
                      className="mediaup__vthumb-btn"
                      onClick={() => patch(it.id, { thumbUrl: null })}
                      disabled={disabled}
                      title="Remove thumbnail (player shows the first frame again)"
                    >
                      Remove
                    </button>
                  )}
                  {it.thumbError && (
                    <span className="mediaup__vthumb-err">{it.thumbError}</span>
                  )}
                </div>
              )}

              <div className="mediaup__tile-actions">
                <button
                  type="button"
                  className="mediaup__move"
                  onClick={() => move(i, -1)}
                  disabled={disabled || i === 0}
                  aria-label="Move earlier"
                  title="Move earlier"
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="mediaup__move"
                  onClick={() => move(i, 1)}
                  disabled={disabled || i === items.length - 1}
                  aria-label="Move later"
                  title="Move later"
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="mediaup__remove"
                  onClick={() => remove(it.id)}
                  disabled={disabled}
                  aria-label="Remove"
                  title="Remove"
                >
                  ×
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}

      {!atCap && (
        <div
          className={`mediaup__drop ${drag ? 'mediaup__drop--over' : ''} ${disabled ? 'mediaup__drop--disabled' : ''}`}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onClick={() => { if (!disabled) input.current?.click(); }}
          onKeyDown={(e) => {
            if (disabled) return;
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.current?.click(); }
          }}
          onDragOver={(e) => { e.preventDefault(); if (!disabled) setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            if (disabled) return;
            addFiles(Array.from(e.dataTransfer.files));
          }}
        >
          <input
            ref={input}
            type="file"
            accept="image/*,video/*"
            multiple
            className="mediaup__input"
            tabIndex={-1}
            onChange={(e) => { addFiles(Array.from(e.target.files ?? [])); e.currentTarget.value = ''; }}
          />
          <p className="mediaup__drop-headline">
            {items.length === 0 ? 'Drop images & videos here' : 'Add more media'}
          </p>
          <p className="mediaup__drop-sub">
            Mixed images and videos, in any order. First item is the cover.
            {' '}({items.length}/{max})
          </p>
        </div>
      )}
      {atCap && (
        <p className="mediaup__cap">Maximum of {max} items reached.</p>
      )}
    </div>
  );
}

/** Map the uploader's ready entries to the ordered WizardMediaItem[] the
 *  publish call expects. Pending/errored entries are dropped. */
export function toWizardMedia(entries: MediaEntry[]): WizardMediaItem[] {
  const out: WizardMediaItem[] = [];
  for (const e of entries) {
    if (e.status !== 'ready') continue;
    if (e.kind === 'video' && e.mediaId) {
      out.push({ kind: 'video', mediaId: e.mediaId, thumbnailUrl: e.thumbUrl ?? null });
    } else if (e.kind === 'image' && e.url) {
      out.push({ kind: 'image', url: e.url, aspectRatio: e.aspectRatio ?? null });
    }
  }
  return out;
}
